// ==========================================================================
// 診断セクション: キーワード選択で商品を絞り込み
// ==========================================================================

import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import $ from 'jquery';

import { diagnosisKeywords } from '../../data/diagnosis-keywords-data.js';
import { colorProducts } from '../../data/color-products-data.js';
import { medicatedProducts } from '../../data/medicated-products-data.js';

const ANIMATION_MS = 350;

// 商品ID → 商品オブジェクト + シリーズ + サムネイル画像パス
const productMap = new Map();
[
	...medicatedProducts.map((p) => ({ product: p, series: 'medicated' })),
	...colorProducts.map((p) => ({ product: p, series: 'color' })),
].forEach(({ product, series }) => {
	productMap.set(product.id, {
		product,
		series,
		thumb: `./assets/images/diagnosis/products/${product.id}.webp`,
	});
});

class Diagnosis {
	constructor() {
		this.$section = $('#diagnosis');
		if (!this.$section.length) return;

		this.$tags = this.$section.find('.diagnosis__tags');
		this.$results = this.$section.find('.diagnosis__results');
		this.$swiperWrapper = this.$section.find('.diagnosis__swiper .swiper-wrapper');
		this.$empty = this.$section.find('.diagnosis__empty');
		this.$resetBtn = this.$section.find('.diagnosis__reset');

		this.selected = new Set();
		this.swiper = null;
		this.animationTimer = null;

		this.init();
	}

	init() {
		this.renderTags();
		this.preloadProductImages();

		// タグクリック（イベント委譲）
		this.$tags.on('click', '.diagnosis__tag', (e) => {
			const $btn = $(e.currentTarget);
			const id = $btn.data('tag-id');
			this.toggleTag(id, $btn);
		});

		// 商品カードクリック（イベント委譲）
		this.$section.on('click', '.diagnosis__product', (e) => {
			const $btn = $(e.currentTarget);
			this.handleProductClick({
				series: $btn.data('series'),
				productId: $btn.data('product-id'),
			});
		});

		// リセットボタン
		this.$resetBtn.on('click', () => this.reset());
	}

	renderTags() {
		const html = diagnosisKeywords.map((kw) => `
			<button class="diagnosis__tag" type="button" data-tag-id="${this.escapeAttr(kw.id)}">${this.escapeHtml(kw.label)}</button>
		`).join('');
		this.$tags.html(html);
	}

	toggleTag(id, $btn) {
		// 無効化タグは無視
		if ($btn.hasClass('is-disabled') || $btn.prop('disabled')) return;

		if (this.selected.has(id)) {
			this.selected.delete(id);
			$btn.removeClass('is-selected');
			$btn.attr('aria-pressed', 'false');
		} else {
			this.selected.add(id);
			$btn.addClass('is-selected');
			$btn.attr('aria-pressed', 'true');
		}
		this.updateResults();

		// 商品が表示される時のみタグ位置までスクロール
		if (this.selected.size > 0) {
			this.scrollToTags();
		}
	}

	reset() {
		this.selected.clear();
		this.$tags.find('.diagnosis__tag')
			.removeClass('is-selected is-disabled')
			.attr('aria-pressed', 'false')
			.prop('disabled', false);

		this.hideResults();

		const section = this.$section.get(0);
		if (section) {
			const header = document.querySelector('.header');
			const offset = header ? header.getBoundingClientRect().height : 0;
			const top = section.getBoundingClientRect().top + window.pageYOffset - offset;
			window.scrollTo({ top, behavior: 'smooth' });
		}
	}

	updateResults() {
		if (this.selected.size === 0) {
			this.updateTagAvailability(new Set());
			this.hideResults();
			return;
		}

		const matched = this.computeMatched();
		this.updateTagAvailability(matched);

		if (matched.size === 0) {
			this.showEmpty();
		} else {
			this.showResults(matched);
		}
	}

	// AND絞り込み: 選択中の全キーワードの products の積集合
	computeMatched() {
		let matched = null;
		diagnosisKeywords.forEach((kw) => {
			if (!this.selected.has(kw.id)) return;
			if (matched === null) {
				matched = new Set(kw.products);
			} else {
				const next = new Set();
				kw.products.forEach((pid) => {
					if (matched.has(pid)) next.add(pid);
				});
				matched = next;
			}
		});
		return matched || new Set();
	}

	buildProductsHtml(matched) {
		// 元の商品データ順を維持（薬用 → カラー）
		const ordered = [
			...medicatedProducts.map((p) => p.id),
			...colorProducts.map((p) => p.id),
		].filter((id) => matched.has(id));

		return ordered
			.map((id) => productMap.get(id))
			.filter(Boolean)
			.map((entry) => this.renderProductCard(entry))
			.join('');
	}

	// 追加選択すると結果0件になる未選択タグを無効化
	// matched.size <= 1 なら以降の絞り込みは意味がないので未選択タグを一律無効化
	updateTagAvailability(matched) {
		const narrowedFully = this.selected.size > 0 && matched.size <= 1;

		this.$tags.find('.diagnosis__tag').each((_, el) => {
			const $btn = $(el);
			const id = $btn.data('tag-id');

			// 選択中 or 何も選択されていない場合は常に有効
			if (this.selected.has(id) || this.selected.size === 0) {
				$btn.removeClass('is-disabled').prop('disabled', false);
				return;
			}

			// 1件以下に絞り込まれている場合は全未選択タグを無効化
			if (narrowedFully) {
				$btn.addClass('is-disabled').prop('disabled', true);
				return;
			}

			const kw = diagnosisKeywords.find((k) => k.id === id);
			if (!kw) return;

			const wouldMatch = kw.products.some((pid) => matched.has(pid));
			$btn.toggleClass('is-disabled', !wouldMatch).prop('disabled', !wouldMatch);
		});
	}

	// 商品ありの結果表示
	showResults(matched) {
		const html = this.buildProductsHtml(matched);
		const isVisible = this.$results.is(':visible');

		this.clearPendingAnim();
		this.$empty.prop('hidden', true);

		if (isVisible && this.swiper) {
			this.refreshSlides(html);
		} else {
			// 初回表示: HTML注入 → slideDown → スライドフェードイン
			this.destroySwiper();
			this.$swiperWrapper.html(html);
			this.$results.stop(true, false).slideDown(ANIMATION_MS);
			this.initSwiper();
			this.animateSlidesIn();
		}
	}

	// 商品サムネイルを先読みしてキャッシュに入れておく
	// → refreshSlides での新レイアウト高さ測定時に img 未ロードで高さが潰れるのを防ぐ
	preloadProductImages() {
		productMap.forEach(({ thumb }) => {
			const img = new Image();
			img.src = thumb;
		});
	}

	// 表示中に差し替える際: 高さを固定 → HTML差替 → 新高さへ height transition → 完了後にスライドフェードイン
	refreshSlides(html) {
		const $swiperEl = this.$section.find('.diagnosis__swiper');
		const swiperEl = $swiperEl[0];
		if (!swiperEl) return;

		// 1. 現在の高さを固定（レイアウト崩れを防ぐ）
		const currentH = swiperEl.offsetHeight;
		$swiperEl.css({ height: currentH + 'px', transition: 'none' });

		// 2. HTMLを差し替え（新スライドは opacity:0, scale(0.7) で透明配置）
		this.$swiperWrapper.html(html);
		if (this.swiper) this.swiper.update();

		// 3. 新レイアウトでの必要高さを測定
		$swiperEl.css('height', 'auto');
		const newH = swiperEl.offsetHeight;
		$swiperEl.css('height', currentH + 'px');

		// 4. 高さに変化がなければ即座にフェードインへ
		if (Math.abs(newH - currentH) < 2) {
			$swiperEl.css({ height: '', transition: '' });
			this.animateSlidesIn();
			return;
		}

		// 5. 強制リフロー → 高さ transition 開始
		void swiperEl.offsetHeight;
		$swiperEl.css({
			transition: `height ${ANIMATION_MS}ms ease`,
			height: newH + 'px',
		});

		// 6. 高さアニメ完了後に lock 解除 → スライドフェードイン
		this.animationTimer = setTimeout(() => {
			$swiperEl.css({ height: '', transition: '' });
			this.animateSlidesIn();
			this.animationTimer = null;
		}, ANIMATION_MS);
	}

	// 該当なしの結果表示
	showEmpty() {
		const isVisible = this.$results.is(':visible');
		this.clearPendingAnim();

		if (isVisible) {
			this.$swiperWrapper.find('.diagnosis__slide-inner').removeClass('is-shown');
			this.animationTimer = setTimeout(() => {
				this.destroySwiper();
				this.$swiperWrapper.empty();
				this.$empty.prop('hidden', false);
			}, ANIMATION_MS);
		} else {
			this.destroySwiper();
			this.$swiperWrapper.empty();
			this.$empty.prop('hidden', false);
			this.$results.stop(true, false).slideDown(ANIMATION_MS);
		}
	}

	// 結果非表示（逆アニメ）
	hideResults() {
		if (!this.$results.is(':visible')) {
			return;
		}

		this.clearPendingAnim();
		this.$swiperWrapper.find('.diagnosis__slide-inner').removeClass('is-shown');

		this.animationTimer = setTimeout(() => {
			this.$results.stop(true, false).slideUp(ANIMATION_MS, () => {
				this.destroySwiper();
				this.$swiperWrapper.empty();
				this.$empty.prop('hidden', true);
			});
		}, ANIMATION_MS);
	}

	// スライド内内包要素を可視状態にトランジションさせる
	animateSlidesIn() {
		const $inners = this.$swiperWrapper.find('.diagnosis__slide-inner');
		if (!$inners.length) return;
		// 強制リフローで初期状態(opacity:0, scale:0.7)を確定させる
		void this.$swiperWrapper[0].offsetHeight;
		requestAnimationFrame(() => {
			$inners.addClass('is-shown');
		});
	}

	clearPendingAnim() {
		if (this.animationTimer) {
			clearTimeout(this.animationTimer);
			this.animationTimer = null;
		}
	}

	// キーワードタグの位置までスムーズスクロール
	scrollToTags() {
		const el = this.$tags.get(0);
		if (!el) return;
		const header = document.querySelector('.header');
		const offset = header ? header.offsetHeight : 0;
		const top = el.getBoundingClientRect().top + window.pageYOffset - offset - 60;
		window.scrollTo({ top, behavior: 'smooth' });
	}

	renderProductCard({ product, series, thumb }) {
		const plainName = this.stripHtmlTags(product.name);
		const hasTag = !!product.tag; // 医薬部外品バッジ
		return `
			<div class="swiper-slide">
				<div class="diagnosis__slide-inner">
					<button class="diagnosis__product" type="button"
						data-series="${this.escapeAttr(series)}"
						data-product-id="${this.escapeAttr(product.id)}">
						<span class="diagnosis__product-thumb">
							<img src="${this.escapeAttr(thumb)}" alt="${this.escapeAttr(plainName)}" width="440" height="540" loading="lazy">
							<span class="diagnosis__product-icon" aria-hidden="true">
								<img src="./assets/images/products/i_plus.svg" alt="" loading="lazy">
							</span>
						</span>
						<span class="diagnosis__product-body">
							<p class="diagnosis__product-name">${this.wrapWordsInline(product.name)}</p>
							${hasTag ? `<span class="diagnosis__product-tag">${this.escapeHtml(product.tag)}</span>` : ''}
						</span>
					</button>
				</div>
			</div>
		`;
	}

	initSwiper() {
		this.destroySwiper();
		this.swiper = new Swiper('.diagnosis__swiper', {
			modules: [Navigation],
			slidesPerView: 'auto',
			spaceBetween: 15,
			grabCursor: true,
			navigation: {
				nextEl: '.diagnosis__btn--next',
				prevEl: '.diagnosis__btn--prev',
			},
			breakpoints: {
				0: {
					slidesPerView: 'auto',
					spaceBetween: 12,
					centeredSlides: true,
				},
				768: {
					slidesPerView: '3',
					spaceBetween: 15,
					centeredSlides: false,
					centerInsufficientSlides: true,
				},
				992: {
					slidesPerView: '5',
					spaceBetween: 15,
					centeredSlides: false,
					centerInsufficientSlides: true,
				},
			},
		});
	}

	destroySwiper() {
		if (this.swiper) {
			this.swiper.destroy(true, true);
			this.swiper = null;
		}
	}

	handleProductClick({ series, productId }) {
		if (!productId) return;
		const eventName = series === 'color' ? 'openColorProductModal' : 'openMedicatedProductModal';
		window.dispatchEvent(new CustomEvent(eventName, { detail: { productId } }));
	}

	// ==========================================================================
	// ヘルパ
	// ==========================================================================

	stripHtmlTags(str) {
		if (!str) return '';
		return String(str).replace(/<[^>]*>/g, '');
	}

	// 半角スペース区切りで各セグメントを inline-block の span で包み、単語途中で改行させない
	wrapWordsInline(str) {
		if (!str) return '';
		const normalized = String(str)
			.replace(/<br\b[^>]*>/gi, ' ')
			.replace(/<[^>]*>/g, '');
		const parts = normalized.split(/\s+/).filter(Boolean);
		return parts
			.map((part) => `<span class="diagnosis__product-name-part">${this.escapeHtml(part)}</span>`)
			.join(' ');
	}

	escapeHtml(str) {
		if (str === null || str === undefined) return '';
		return String(str)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	escapeAttr(str) {
		if (str === null || str === undefined) return '';
		return String(str).replace(/"/g, '&quot;');
	}
}

$(() => {
	new Diagnosis();
});

export default Diagnosis;
